import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { leaveParty } from "../../services/partyService.js";

/**
 * The way out of a room you did not mean to be in.
 *
 * `POST /api/party/leave` and `leaveParty()` have both existed since phase 3
 * with **no caller**, so the only way to give up a seat was to clear a cookie —
 * which is not something to ask of somebody who mistyped a code at a party.
 *
 * # Two taps, and no browser dialog
 *
 * `window.confirm` blocks the whole page and reads as a browser artefact on a
 * phone; the confirmation is in the page instead. It is confirmed at all because
 * leaving is not quite free — see the warning it carries.
 *
 * # What leaving actually costs, said plainly
 *
 * The seat is soft-deleted (`party_members.left_at`) and rejoining finds the
 * *same* seat — but only for the same profile. A signed-in player is their
 * account, so they come back to their own timeline; a guest's only link to their
 * profile was the device cookie this route clears, so coming back means a new
 * name and an empty timeline. That is worth one sentence before the tap rather
 * than a surprise after it.
 *
 * # Phase 16f-1: it is rendered during a game too, and says a different thing
 *
 * This component existed from phase 3 and was rendered in exactly one place —
 * `LobbyPanel`, the pre-game screen. So the party flow's only exit lived on the
 * one screen nobody needs it on, and from the moment a game started there was no
 * way out but the browser's Back button.
 *
 * Mid-game the seat is still soft-deleted and the tokens still revoked; what
 * changes is what the room sees. The engine holds sides by id, so the cards stay
 * on the board and the side reads as away — which is worth saying, because
 * "leave" during a game sounds like it might forfeit or delete something.
 */
export function LeaveParty({
  isHost,
  inGame = false,
}: {
  isHost: boolean;
  /**
   * Whether a game is actually running, which changes what leaving costs.
   *
   * The lobby warning is about coming *back*; this one is about what happens to
   * the board you are walking away from.
   */
  inGame?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [asked, setAsked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!asked) {
    return (
      // **A button, not a link** — 20d. It was already a `<button>`; it was
      // *dressed* as underlined text, which is the one style on these screens
      // that means "this navigates somewhere". Leaving a party is an action
      // with a consequence, and the only one in the powers column that did not
      // look like the things around it.
      //
      // ⚠️ Quiet, and only red on approach: it sits under six shop items that
      // are all things you might want, and a permanently red control next to
      // them would read as an error state on the screen rather than as an exit.
      // The confirmation behind it is where the real warning lives.
      <button
        type="button"
        onClick={() => {
          setAsked(true);
        }}
        className="mt-2 min-h-12 w-full rounded-lg border border-white/15 px-4 text-sm font-medium text-white/60 transition-colors hover:border-wrong-500/60 hover:text-wrong-500"
      >
        {t("leave.open")}
      </button>
    );
  }

  const leave = () => {
    setBusy(true);
    setError(null);
    void leaveParty().then(
      () => {
        // `replace`, so Back does not return to a party this browser no longer
        // holds a seat in.
        void navigate("/", { replace: true });
      },
      (cause: unknown) => {
        setError(cause instanceof Error ? cause.message : t("round.refused"));
        setBusy(false);
      },
    );
  };

  return (
    <section aria-label={t("leave.label")} className="mt-2 flex flex-col gap-2">
      {inGame && (
        <p className="text-center text-sm text-white/60">{t("leave.warnInGame")}</p>
      )}
      <p className="text-center text-sm text-white/60">
        {isHost ? t("leave.warnHost") : t("leave.warnGuest")}
      </p>
      {error && (
        <p role="alert" className="text-center text-sm text-wrong-500">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={leave}
          className="min-h-12 flex-1 rounded-lg border border-wrong-500/60 font-medium text-wrong-500 disabled:opacity-40"
        >
          {busy ? t("leave.leaving") : t("leave.confirm")}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setAsked(false);
          }}
          className="min-h-12 flex-1 rounded-lg border border-white/20 font-medium disabled:opacity-40"
        >
          {t("leave.stay")}
        </button>
      </div>
    </section>
  );
}
